import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, Users, ArrowRight } from 'lucide-react';
import { getEvents, getImageUrl, formatEventDate, isUpcomingEvent } from '../utils/api';
import './EventsPage.css';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await getEvents();
      // Sort events by date
      const sorted = (data || []).sort((a, b) => new Date(a.date + 'T12:00:00') - new Date(b.date + 'T12:00:00'));
      setEvents(sorted);
    } catch (error) {
      console.error('Failed to load events', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const result = formatEventDate(dateStr);
    // Extract year separately
    const year = dateStr ? dateStr.split('-')[0] : '';
    return {
      day: result.day,
      month: result.month,
      year: year,
      full: result.full
    };
  };

  if (loading) {
    return (
      <div className="events-page">
        <section className="events-hero">
          <div className="container">
            <h1>Events</h1>
            <p>Experience the sound live</p>
          </div>
        </section>
        <section className="events-content">
          <div className="container">
            <div className="loading-spinner">Loading events...</div>
          </div>
        </section>
      </div>
    );
  }

  const upcomingEvents = events.filter(e => isUpcomingEvent(e.date));
  const pastEvents = events.filter(e => !isUpcomingEvent(e.date));

  return (
    <div className="events-page">
      {/* Hero Section */}
      <section className="events-hero">
        <div className="events-hero-overlay"></div>
        <div className="container events-hero-content">
          <h1>Events</h1>
          <p>Join us at exclusive label showcases around the world</p>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="events-content">
        <div className="container">
          <h2 className="section-title">
            <span className="title-accent"></span>
            Upcoming Events
          </h2>

          {upcomingEvents.length === 0 ? (
            <div className="no-events">
              <p>No upcoming events at the moment. Check back soon!</p>
              <Link to="/contact" className="btn btn-secondary">Get Notified</Link>
            </div>
          ) : (
            <div className="events-list">
              {upcomingEvents.map((event) => {
                const date = formatDate(event.date);
                
                return (
                  <div key={event.id} className="event-card-large" data-testid={`event-${event.id}`}>
                    <div className="event-date-block">
                      <span className="event-month">{date.month}</span>
                      <span className="event-day">{date.day}</span>
                      <span className="event-year">{date.year}</span>
                    </div>

                    <div className="event-image-block">
                      {event.image && (
                        <img src={getImageUrl(event.image)} alt={event.title} loading="lazy" />
                      )}
                    </div>

                    <div className="event-info-block">
                      <h3>{event.title}</h3>
                      
                      <div className="event-meta">
                        <div className="meta-item">
                          <MapPin size={16} />
                          <span>{event.venue}, {event.city}</span>
                        </div>
                        <div className="meta-item">
                          <Clock size={16} />
                          <span>{event.time}</span>
                        </div>
                        {event.lineup && event.lineup.length > 0 && (
                          <div className="meta-item">
                            <Users size={16} />
                            <span>{event.lineup.slice(0, 3).join(', ')}{event.lineup.length > 3 ? '...' : ''}</span>
                          </div>
                        )}
                      </div>

                      {event.description && (
                        <p className="event-description">{event.description}</p>
                      )}

                      <Link to={`/events/${event.slug || event.id}`} className="event-cta" data-testid={`event-link-${event.id}`}>
                        Find out more
                        <ArrowRight size={18} />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Past Events */}
          {pastEvents.length > 0 && (
            <>
              <h2 className="section-title past-events-title">
                <span className="title-accent"></span>
                Past Events
              </h2>
              
              <div className="past-events-grid">
                {pastEvents.map((event) => {
                  const date = formatDate(event.date);
                  
                  return (
                    <div key={event.id} className="past-event-card">
                      <div className="past-event-image">
                        {event.image && <img src={getImageUrl(event.image)} alt={event.title} loading="lazy" />}
                        <div className="past-event-date">{date.month} {date.day}, {date.year}</div>
                      </div>
                      <div className="past-event-info">
                        <h4>{event.title}</h4>
                        <p>{event.venue}, {event.city}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="events-cta-section">
        <div className="container">
          <h2>Want Us at Your Venue?</h2>
          <p>We're always looking for new spaces to bring our sound. Get in touch to discuss hosting an AMR event.</p>
          <Link to="/contact" className="btn btn-primary">Contact Us</Link>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
